import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["className", "status", "iconDescription", "description", "onSuccess", "successDelay"];
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useRef, useEffect } from 'react';
import cx from 'classnames';
import { ErrorFilled, CheckmarkFilled } from '@carbon/icons-react';
import Loading from '../Loading/Loading.js';
import { usePrefix } from '../../internal/usePrefix.js';
var InlineLoading = function InlineLoading(_ref) {
  var _rest$ariaLive;
  var className = _ref.className,
    _ref$status = _ref.status,
    status = _ref$status === void 0 ? 'active' : _ref$status,
    iconDescription = _ref.iconDescription,
    description = _ref.description,
    onSuccess = _ref.onSuccess,
    _ref$successDelay = _ref.successDelay,
    successDelay = _ref$successDelay === void 0 ? 1500 : _ref$successDelay,
    rest = _objectWithoutProperties(_ref, _excluded);
  var prefix = usePrefix();
  var loadingClasses = cx("".concat(prefix, "--inline-loading"), className);
  var timerRef = useRef(null);
  useEffect(function () {
    if (status === 'finished') {
      timerRef.current = setTimeout(function () {
        if (onSuccess) {
          onSuccess();
        }
      }, successDelay);
    }
    return function () {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [status, onSuccess, successDelay]);
  var getLoading = function getLoading() {
    var iconLabel = iconDescription ? iconDescription : status;
    if (status === 'error') {
      return /*#__PURE__*/React.createElement(ErrorFilled, {
        className: "".concat(prefix, "--inline-loading--error")
      }, /*#__PURE__*/React.createElement("title", null, iconLabel));
    }
    if (status === 'finished') {
      return /*#__PURE__*/React.createElement(CheckmarkFilled, {
        className: "".concat(prefix, "--inline-loading__checkmark-container")
      }, /*#__PURE__*/React.createElement("title", null, iconLabel));
    }
    if (status === 'active') {
      if (!iconDescription) {
        iconLabel = 'loading';
      }
      return /*#__PURE__*/React.createElement(Loading, {
        small: true,
        description: iconLabel,
        withOverlay: false,
        active: status === 'active'
      });
    }
    if (status === 'inactive') {
      if (!iconDescription) {
        iconLabel = 'not loading';
      }
      return /*#__PURE__*/React.createElement("title", {
        className: "".concat(prefix, "--inline-loading__inactive-status")
      }, iconLabel);
    }
    return undefined;
  };
  var loadingText = description && /*#__PURE__*/React.createElement("div", {
    className: "".concat(prefix, "--inline-loading__text")
  }, description);
  var loading = getLoading();
  var loadingAnimation = loading && /*#__PURE__*/React.createElement("div", {
    className: "".concat(prefix, "--inline-loading__animation")
  }, loading);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: loadingClasses
  }, rest, {
    "aria-live": (_rest$ariaLive = rest['aria-live']) !== null && _rest$ariaLive !== void 0 ? _rest$ariaLive : 'assertive'
  }), loadingAnimation, loadingText);
};
export { InlineLoading as default };